import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Download, Loader2, Plus, Star, Store, Trash2, X } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { VendorRecord } from "@/components/vendors/VendorRecord"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  KIND_LABEL,
  VENDOR_KINDS,
  createVendor,
  deleteVendor,
  vendorsQueryOptions,
  type DeleteVendorDryRun,
  type Vendor,
  type VendorKind,
} from "@/core/vendors"

// The Vendors LAYOUT route. Route DEPTH = Main columns (mirrors lodging.tsx):
//   /vendors             → col 1 (table)
//   /vendors/$vendorId   → col 1 + 2 (+ vendor record)
// `$vendorId` lives on a MARKER child route; ColumnLayout decides how many fit.
// `?focus=2` fullscreens col 2. Legacy `?vendorId=…` redirects into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/vendors")({
  validateSearch: (s: Record<string, unknown>): { focus?: number; vendorId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      // Legacy — accepted only so beforeLoad can redirect it into the path.
      vendorId: typeof s.vendorId === "string" ? s.vendorId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.vendorId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/vendors/$vendorId",
        params: { ...params, vendorId: search.vendorId },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    // Vendors are org-scoped (no eventId arg) — call vendorsQueryOptions() the
    // same way the component does. Still resolve eventId first so the active org
    // is pinned before the prefetch's JWT mint. All in the background so the
    // resolve never gates navigation.
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(vendorsQueryOptions())
        .catch(() => undefined)
    })
  },
  component: VendorsView,
})

// Stable per-kind tint for the leading avatar disc — mirrors the mockup's
// gradient initials so each category reads at a glance.
const KIND_TINT: Record<VendorKind, string> = {
  agency: "from-sky-400 to-indigo-500",
  rental: "from-amber-400 to-orange-600",
  ground: "from-emerald-400 to-teal-600",
  lodging: "from-rose-400 to-pink-600",
  other: "from-plum-400 to-plum-600",
}

function kindLabel(k: string): string {
  return KIND_LABEL[k as VendorKind] ?? "Other"
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

function contactLine(v: Vendor): string {
  return v.contactName ?? v.email ?? v.phone ?? ""
}

function VendorsView() {
  const { eventId } = useActiveEvent()
  const params = useParams({ from: "/$orgSlug/$eventSlug/vendors" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)

  const { vendorId: selectedId } = useRouteRecordSelection("vendorId")

  const vendorsQ = useQuery({ ...vendorsQueryOptions(), enabled: !!eventId })
  const vendors = vendorsQ.data ?? []

  // Nav: drilling into a vendor CLEARS any fullscreen focus (you want to see
  // what you just opened, in context). ColumnLayout collapses to the record
  // column when the container is too narrow to show both.
  const openVendor = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/vendors/$vendorId",
        params: { ...params, vendorId: id },
        search: {},
      }),
    [navigate, params],
  )
  const openVendorFull = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/vendors/$vendorId",
        params: { ...params, vendorId: id },
        search: { focus: 2 },
      }),
    [navigate, params],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/vendors", params, search: {} }),
    [navigate, params],
  )
  // Toggle col-2 fullscreen from the record's header expand button (mirrors
  // people.tsx). Clicking again restores the split.
  const toggleFocus = useCallback(
    (n: number) => {
      const next = focus === n ? undefined : n
      if (selectedId)
        void navigate({
          to: "/$orgSlug/$eventSlug/vendors/$vendorId",
          params: { ...params, vendorId: selectedId },
          search: { focus: next },
        })
      else void navigate({ to: "/$orgSlug/$eventSlug/vendors", params, search: { focus: next } })
    },
    [navigate, params, selectedId, focus],
  )

  const config = useMemo<CollectionConfig<Vendor>>(() => {
    const exportCsv = (rows: Vendor[]) => {
      downloadMappedCsv(
        "vendors.csv",
        ["Vendor", "Category", "Contact", "Email", "Phone", "Default"],
        rows,
        (v) => [
          v.name,
          kindLabel(v.kind),
          v.contactName ?? "",
          v.email ?? "",
          v.phone ?? "",
          v.isDefault === 1 ? "Yes" : "",
        ],
      )
    }

    const removeVendors = async (rows: Vendor[]) => {
      const targets: Vendor[] = []
      let contacts = 0
      for (const v of rows) {
        const preview: DeleteVendorDryRun = await deleteVendor(v.id, true)
        targets.push(v)
        contacts += preview.contactCount
      }
      const names = targets.map((v) => v.name).join(", ")
      const contactNote =
        contacts > 0 ? ` This also removes ${contacts} contact(s) and their invites.` : ""
      if (!confirm(`Remove ${targets.length} vendor(s) — ${names}?${contactNote}`)) return
      for (const v of targets) await deleteVendor(v.id, false)
      toast.success(`Removed ${targets.length} vendor(s)`)
      await vendorsQ.refetch()
    }

    return {
      title: "Vendors",
      getId: (v) => v.id,
      searchText: (v) => `${v.name} ${kindLabel(v.kind)} ${contactLine(v)} ${v.email ?? ""}`,
      onOpen: (v) => openVendor(v.id),
      // Right-click context menu: open / fullscreen / new-tab / new-window all
      // target the nested record column. Fullscreen forces the record column.
      getRecordHref: (v) => `/${params.orgSlug}/${params.eventSlug}/vendors/${v.id}`,
      onOpenFullscreen: (v) => openVendorFull(v.id),
      onNew: () => setCreateOpen(true),
      newLabel: "Add vendor",
      defaultSortId: "name",
      defaultSortDir: "asc",
      emptyTitle: "No vendors yet",
      emptyBlurb: "Add your travel agency and it becomes the flights default.",
      emptyIcon: <Store className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "default", label: "Defaults", filter: (v) => v.isDefault === 1 },
        { id: "with-contact", label: "With contact", filter: (v) => contactLine(v) !== "" },
      ],
      filters: [
        {
          id: "kind",
          label: "Category",
          options: [
            ["all", "All"],
            ["agency", "Travel agency"],
            ["rental", "Rental cars"],
            ["ground", "Ground"],
            ["lodging", "Lodging"],
            ["other", "Other"],
          ],
          predicate: (val, v) => v.kind === val,
        },
      ],
      groupOptions: [
        {
          id: "kind",
          label: "Category",
          groupOf: (v) => kindLabel(v.kind),
        },
        {
          id: "status",
          label: "Status",
          groupOf: (v) => (v.isDefault === 1 ? "Default" : "Listed"),
          order: (l) => (l === "Default" ? 0 : 1),
        },
      ],
      bulkActions: [
        {
          id: "export",
          label: "Export",
          icon: <Download className="h-4 w-4" />,
          run: exportCsv,
        },
        {
          id: "remove",
          label: "Remove",
          icon: <Trash2 className="h-4 w-4" />,
          run: (rows) => void removeVendors(rows),
        },
      ],
      columns: [
        {
          id: "name",
          header: "Vendor",
          sortable: true,
          sortValue: (v) => v.name.toLowerCase(),
          render: (v) => (
            <div className="flex items-center gap-2.5">
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-gradient-to-br ${KIND_TINT[v.kind] ?? KIND_TINT.other} text-[10px] font-bold text-white`}
              >
                {initials(v.name)}
              </span>
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {v.name || "Untitled vendor"}
              </span>
              {v.isDefault === 1 && (
                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
              )}
            </div>
          ),
        },
        {
          id: "category",
          header: "Category",
          className: "text-plum-700/70 dark:text-plum-200/55",
          sortable: true,
          sortValue: (v) => kindLabel(v.kind),
          render: (v) => kindLabel(v.kind),
        },
        {
          id: "contact",
          header: "Contact",
          className: "text-plum-700/75 dark:text-plum-200/60",
          render: (v) => {
            const line = contactLine(v)
            return line ? (
              line
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">No contact</span>
            )
          },
        },
        {
          id: "status",
          header: "Status",
          render: (v) =>
            v.isDefault === 1 ? (
              <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                Default
              </span>
            ) : (
              <span className="rounded-md bg-plum-100 px-2 py-0.5 text-[11px] font-semibold text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45">
                Listed
              </span>
            ),
        },
      ],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openVendor, openVendorFull, params, vendorsQ])

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [
      {
        id: "list",
        minWidth: 320,
        width: 420,
        node: (
          <CollectionView
            config={config}
            rows={vendors}
            activeId={selectedId}
            loading={vendorsQ.isLoading && vendors.length === 0}
            error={
              vendorsQ.isError
                ? ((vendorsQ.error as Error)?.message ?? "Failed to load vendors")
                : null
            }
          />
        ),
      },
    ]
    if (selectedId) {
      cols.push({
        id: "record",
        minWidth: 520,
        fill: true,
        node: (
          <VendorRecord
            key={selectedId}
            vendorId={selectedId}
            onClose={closePane}
            onFullscreen={() => toggleFocus(2)}
          />
        ),
      })
    }
    return cols
  }, [
    config,
    vendors,
    selectedId,
    vendorsQ.isLoading,
    vendorsQ.isError,
    vendorsQ.error,
    closePane,
    toggleFocus,
  ])

  return (
    <RoutedCollectionLayout
      columns={columns}
      focus={focus}
      className="relative flex min-h-0 flex-1 flex-col"
    >
      {createOpen && (
        <CreateVendorDialog
          vendors={vendors}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false)
            openVendor(id)
          }}
        />
      )}
    </RoutedCollectionLayout>
  )
}

// Lightweight create dialog — the first vendor of a kind becomes its ★ default
// automatically (mirrors the admin SPA). On success we drill straight into the
// new record so the user can flesh out contacts/notes.
function CreateVendorDialog({
  vendors,
  onClose,
  onCreated,
}: {
  vendors: Vendor[]
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const qc = useQueryClient()
  const [kind, setKind] = useState<VendorKind>("agency")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [busy, setBusy] = useState(false)

  async function commit() {
    if (!name.trim()) return
    setBusy(true)
    try {
      const isDefault = vendors.some((v) => v.kind === kind && v.isDefault === 1) ? 0 : 1
      const created = await createVendor({
        kind,
        name,
        email: email.trim() || null,
        phone: phone.trim() || null,
        isDefault,
      })
      await qc.invalidateQueries({ queryKey: ["resource", "vendors"] })
      onCreated(created.id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add the vendor")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 w-[min(94%,28rem)] rounded-2xl bg-white p-5 shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">New vendor</h2>
          <button
            onClick={onClose}
            className="ml-auto grid h-7 w-7 place-items-center rounded text-plum-700/55 transition hover:bg-plum-100/60 dark:text-plum-200/45 dark:hover:bg-white/5"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[11px] leading-tight text-plum-700/55 dark:text-plum-200/40">
          The first vendor of a category becomes its ★ default automatically.
        </p>
        <div className="space-y-2.5">
          <div>
            <DialogLabel>Category</DialogLabel>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as VendorKind)}
              className="mt-0.5 h-8 w-full rounded-md border border-plum-200/70 bg-white px-2 text-[12.5px] outline-none transition focus:border-plum-400 dark:border-white/8 dark:bg-white/5 dark:text-plum-50"
            >
              {VENDOR_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
          <DialogInput label="Name" value={name} onChange={setName} autoFocus placeholder="Acme Travel" />
          <DialogInput label="Email" value={email} onChange={setEmail} placeholder="bookings@acmetravel.com" />
          <DialogInput label="Phone" value={phone} onChange={setPhone} placeholder="+1 555 555 0142" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="h-9 rounded-lg px-3 text-[13px] font-semibold text-plum-700/70 transition hover:bg-plum-100/60 disabled:opacity-50 dark:text-plum-200/55 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            onClick={commit}
            disabled={busy || !name.trim()}
            className="flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold text-white shadow-fluent-sm transition disabled:opacity-50"
            style={{ background: "var(--accent)" }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add vendor
          </button>
        </div>
      </div>
    </div>
  )
}

function DialogLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-bold tracking-[0.06em] text-plum-700/50 uppercase dark:text-plum-200/35">
      {children}
    </div>
  )
}

function DialogInput({
  label,
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  return (
    <div>
      <DialogLabel>{label}</DialogLabel>
      <input
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 h-8 w-full rounded-md border border-plum-200/70 bg-white px-2.5 text-[12.5px] outline-none transition placeholder:text-plum-700/35 focus:border-plum-400 dark:border-white/8 dark:bg-white/5 dark:text-plum-50"
      />
    </div>
  )
}
